import { FieldTimeOutlined } from '@ant-design/icons';
import { Avatar, Space, Typography } from 'antd';
import type { FC } from 'react';
import { useDepartments } from '@/common/useDepartments';
import type { GetDriversWithLastTrackPointQuery } from '@/generated/graphql/graphql';
import dayjs from '@/utils/dayjs-instance';
import {
  countExpressOrders,
  getExpressDriverColor,
  getMinutesLeftLikeSchedule,
  isExpressOrder,
} from '../utils/expressOrdersUtils';
import { ExpressBadge } from './ExpressBadge';
import { OrderScheduleInfo } from './OrderScheduleInfo';

const { Text } = Typography;

const minutesFromHours = (hours?: number | null): number => (hours ?? 0) * 60;

export const DriverTile: FC<{
  driver: GetDriversWithLastTrackPointQuery['user'][0];
  onSelect: () => void;
  selected: boolean;
}> = ({ driver, onSelect, selected }) => {
  const { selectedDepartment } = useDepartments();
  const departmentTimeZone = selectedDepartment?.timeZone ?? 0;

  return (
    <button
      type="button"
      className="driver-tile"
      onClick={() => onSelect()}
      style={{
        background: selected ? 'rgba(5, 5, 5, 0.06)' : 'transparent',
        padding: '12px 16px',
        border: 'none',
        width: '100%',
        textAlign: 'left',
        display: 'block',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <div className="driver-tile__meta" style={{ width: '100%' }}>
        <div className="driver-tile__avatar">
          <Avatar
            size={48}
            icon={driver.name[0]}
            style={{
              minWidth: '48px',
              minHeight: '48px',
              flexShrink: 0,
            }}
          />
        </div>
        <div className="driver-tile__content">
          <div
            className="driver-tile__title"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              width: '100%',
              minWidth: 0,
            }}
          >
            <DriverTileTitle
              driver={driver}
              delivered={driver.delivered?.aggregate?.count || 0}
              returned={driver.returned?.aggregate?.count || 0}
              reschedule={driver.rescheduled?.aggregate?.count || 0}
              departmentTimeZone={departmentTimeZone}
            />
          </div>
          <div className="driver-tile__description">
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {driver.email}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

const DriverTileTitle: FC<{
  driver: GetDriversWithLastTrackPointQuery['user'][0];
  delivered: number;
  returned: number;
  reschedule: number;
  departmentTimeZone: number;
}> = ({ delivered, returned, reschedule, driver, departmentTimeZone }) => {
  const batteryLevel =
    driver.track_points[0]?.batteryLevel !== null &&
    driver.track_points[0]?.batteryLevel !== undefined
      ? Math.round(Number(driver.track_points[0].batteryLevel) * 100)
      : -1;

  const ordersWithSchedule =
    driver.orders?.filter((order) => order?.plannedDate && order?.plannedDateDuration != null) ??
    [];
  const activeExpressWithMinutes = ordersWithSchedule
    .filter((order) => isExpressOrder(order) && order.statusName !== 'Delivered')
    .map((order) => {
      const minutesLeft = getMinutesLeftLikeSchedule(order, departmentTimeZone);
      return { order, minutesLeft };
    })
    .filter(({ minutesLeft }) => minutesLeft != null && minutesLeft > 0);

  const scheduleOrder = activeExpressWithMinutes.length
    ? activeExpressWithMinutes.sort((a, b) => (a.minutesLeft ?? 0) - (b.minutesLeft ?? 0))[0].order
    : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        fontSize: '16px',
        width: '100%',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: '60%',
            flexShrink: 1,
            minWidth: 0,
            gap: '8px',
          }}
        >
          <Text
            style={{
              fontWeight: '500',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 1,
            }}
          >
            {driver.name}
          </Text>
          {driver.orders && countExpressOrders(driver.orders, departmentTimeZone) > 0 && (
            <ExpressBadge
              color={getExpressDriverColor(driver.orders, departmentTimeZone) || '#FFD700'}
            />
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Text
              style={{
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 4,
                color: 'rgb(24, 144, 255)',
                fontWeight: 200,
                fontSize: '12px',
              }}
            >
              <FieldTimeOutlined />
              {driver.track_points[0]
                ? dayjs
                    .utc(driver.track_points[0].time)
                    .utcOffset(minutesFromHours(departmentTimeZone))
                    .format('HH:mm')
                : '<no geo>'}
            </Text>

            {batteryLevel !== -1 && (
              <div className="driver-battery-level">
                <span className="driver-battery-level--text">{batteryLevel}%</span>
                <div
                  className="driver-battery-level--progress"
                  style={{
                    width: 0.35 * batteryLevel - 2,
                    background: getBatteryColor(batteryLevel),
                  }}
                ></div>
              </div>
            )}
          </div>

          {scheduleOrder && (
            <OrderScheduleInfo
              plannedDate={scheduleOrder.plannedDate}
              plannedDateDuration={scheduleOrder.plannedDateDuration ?? undefined}
              departmentTimeZone={departmentTimeZone}
              isExpress={isExpressOrder(scheduleOrder)}
              statusName={scheduleOrder.statusName ?? undefined}
            />
          )}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Space
          style={{
            background: 'rgba(24, 144, 255, .05)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
          }}
          orientation="horizontal"
        >
          <Text type="success" style={{ fontSize: '12px' }}>
            {delivered}
          </Text>
          <Text type="danger" style={{ fontSize: '12px' }}>
            {returned}
          </Text>
          <Text type="warning" style={{ fontSize: '12px' }}>
            {reschedule}
          </Text>
        </Space>
      </div>
    </div>
  );
};

export function getBatteryColor(batteryLevel: number): string {
  let color = 'red';
  if (batteryLevel >= 20 && batteryLevel < 40) {
    color = 'orange';
  } else if (batteryLevel >= 40 && batteryLevel < 60) {
    color = '#ff4';
  } else if (batteryLevel >= 60) {
    color = '#2e2';
  }
  return color;
}
